import { useEffect, useState } from "react";
import { apiClient } from "@/lib/auth";
import AnimatedCounter from "@/components/AnimatedCounter";
import { Users, Building2, PenSquare, Calendar, CreditCard, Shield, Activity, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const STAT_CARDS = [
  { key: "total_users", label: "Utilisateurs", icon: Users, color: "from-primary to-violet-500" },
  { key: "total_businesses", label: "Profils business", icon: Building2, color: "from-blue-500 to-cyan-400" },
  { key: "total_generations", label: "Contenus rédigés", icon: PenSquare, color: "from-violet-500 to-pink-500" },
  { key: "paid_transactions", label: "Paiements réussis", icon: CreditCard, color: "from-emerald-500 to-teal-400" },
];

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [tx, setTx] = useState([]);
  const [diag, setDiag] = useState(null);
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);

  useEffect(() => {
    apiClient.get("/admin/users").then(r => setUsers(r.data)).catch(()=>{});
    apiClient.get("/admin/analytics").then(r => setAnalytics(r.data)).catch(()=>{});
    apiClient.get("/admin/transactions").then(r => setTx(r.data)).catch(()=>{});
    apiClient.get("/admin/stripe-diag").then(r => setDiag(r.data)).catch(()=>{});
  }, []);

  const runProbe = async () => {
    setProbing(true); setProbe(null);
    try {
      const r = await apiClient.post("/admin/stripe-diag/probe");
      setProbe(r.data);
    } catch (e) {
      setProbe({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setProbing(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary to-violet-500 flex items-center justify-center text-white">
          <Shield className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary">Administration</p>
          <h1 className="text-3xl font-black tracking-tighter font-display">Pilotage global</h1>
        </div>
      </div>

      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {STAT_CARDS.map(s => {
            const Icon = s.icon;
            return (
              <div key={s.key} className="relative card-hover border border-border rounded-xl p-5 bg-card overflow-hidden" data-testid={`stat-${s.label}`}>
                <div className={`absolute -top-12 -right-12 h-32 w-32 rounded-full bg-gradient-to-br ${s.color} opacity-20 blur-2xl`} />
                <div className="relative">
                  <div className={`h-8 w-8 rounded-md bg-gradient-to-br ${s.color} flex items-center justify-center text-white mb-3`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{s.label}</p>
                  <div className="text-3xl font-black font-display mt-1 tabular-nums">
                    <AnimatedCounter value={analytics[s.key] || 0} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {analytics?.plan_distribution && (
        <div className="border border-border rounded-2xl p-6 bg-card">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground mb-4">Distribution des abonnements</p>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(analytics.plan_distribution).map(([plan, count]) => {
              const total = Object.values(analytics.plan_distribution).reduce((a, b) => a + b, 0) || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={plan} className="border border-border rounded-xl p-4">
                  <p className="text-xs uppercase tracking-wider font-bold capitalize">{plan}</p>
                  <div className="text-3xl font-black font-display mt-1 tabular-nums">{count}</div>
                  <div className="h-1.5 mt-3 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-primary to-violet-500 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">{pct}% des utilisateurs</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="border border-border rounded-2xl bg-card overflow-hidden" data-testid="stripe-diag-panel">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            <p className="text-sm font-bold">Diagnostic Stripe (production)</p>
          </div>
          <button
            data-testid="stripe-probe-btn"
            onClick={runProbe}
            disabled={probing}
            className="text-xs px-3 py-1.5 rounded-md bg-primary text-primary-foreground font-bold uppercase tracking-wider hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {probing ? <><Loader2 className="h-3 w-3 animate-spin" /> Test en cours…</> : "Tester une session réelle"}
          </button>
        </div>
        <div className="p-5 grid md:grid-cols-2 gap-4 text-sm">
          {!diag ? (
            <p className="text-muted-foreground col-span-2">Chargement…</p>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Configuration clé</p>
                <Row label="Clé détectée" value={diag.key_masked} mono />
                <Row label="Mode détecté" value={
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                    diag.stripe_mode === "live" ? "bg-emerald-500/10 text-emerald-600" :
                    diag.stripe_mode === "test" ? "bg-amber-500/10 text-amber-600" :
                    "bg-muted text-muted-foreground"
                  }`}>{diag.stripe_mode}</span>
                } />
                <Row label="Préfixe sk_live_" value={diag.starts_with_sk_live ? <Yes/> : <No/>} />
                <Row label="Espaces / quotes nettoyés" value={diag.had_whitespace_or_quotes ? <Yes warn/> : <No ok/>} />
              </div>
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">API Base</p>
                <Row label="Attendu" value={diag.expected_api_base} mono />
                <Row label="Actuel" value={diag.current_api_base} mono />
                <Row label="API base correcte" value={diag.api_base_correct ? <Yes ok/> : <No err/>} />
                <Row label="Proxy test Emergent" value={diag.is_emergent_test_proxy ? <Yes warn/> : <No ok/>} />
              </div>
            </>
          )}
        </div>
        {probe && (
          <div className="border-t border-border p-5 bg-muted/20" data-testid="stripe-probe-result">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Résultat du test (session Stripe réelle)</p>
            {!probe.ok ? (
              <div className="flex items-start gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-rose-500 mt-0.5" />
                <div>
                  <p className="font-bold text-rose-600">Erreur Stripe</p>
                  <p className="text-muted-foreground font-mono text-xs mt-1 break-all">{probe.error}</p>
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Row label="Préfixe session_id" value={probe.session_id_prefix} mono />
                  <Row label="Type de session" value={
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                      probe.session_kind === "live" ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"
                    }`}>{probe.session_kind === "live" ? "cs_live_ (LIVE)" : `cs_${probe.session_kind}_`}</span>
                  } />
                  <Row label="Hôte checkout" value={probe.checkout_url_host} mono />
                </div>
                <div className="space-y-2">
                  <Row label="Mode clé vs session" value={probe.match ? <Yes ok label="cohérent"/> : <No err label="INCOHÉRENT"/>} />
                  {probe.session_kind === "live" ? (
                    <p className="text-xs text-emerald-600 font-medium leading-relaxed">
                      ✓ Stripe répond en mode LIVE. Les paiements sont réels. La bannière « test » sur Stripe Checkout ne devrait pas apparaître.
                    </p>
                  ) : probe.stripe_mode === "live" ? (
                    <p className="text-xs text-rose-600 font-medium leading-relaxed">
                      ❌ La clé est sk_live_ mais Stripe renvoie une session test. Cela arrive quand le compte Stripe n'est pas encore activé (KYC en attente) ou que la clé est invalide. Vérifiez votre tableau de bord Stripe → Activate Account.
                    </p>
                  ) : (
                    <p className="text-xs text-amber-600 font-medium leading-relaxed">
                      ⚠ La clé est en mode TEST. Mettez à jour STRIPE_API_KEY avec votre clé sk_live_ dans les secrets de votre déploiement, puis redéployez.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border border-border rounded-2xl bg-card overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <p className="text-sm font-bold">Utilisateurs</p>
          <span className="text-xs text-muted-foreground">{users.length} compte(s)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="border-b border-border text-left text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground bg-muted/20">
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Nom</th>
                <th className="px-5 py-3">Plan</th>
                <th className="px-5 py-3">Rôle</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} className="border-b border-border last:border-0 hover:bg-accent/30 transition-colors">
                  <td className="px-5 py-3 font-mono text-xs">{u.email}</td>
                  <td className="px-5 py-3 font-medium">{u.name}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                      u.plan === "business" ? "bg-amber-500/10 text-amber-600" :
                      u.plan === "pro" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                    }`}>{u.plan}</span>
                  </td>
                  <td className="px-5 py-3 text-muted-foreground">{u.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border border-border rounded-2xl bg-card overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <p className="text-sm font-bold">Transactions</p>
          <span className="text-xs text-muted-foreground">{tx.length} transaction(s)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="tx-table">
            <thead>
              <tr className="border-b border-border text-left text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground bg-muted/20">
                <th className="px-5 py-3">Session</th>
                <th className="px-5 py-3">Plan</th>
                <th className="px-5 py-3">Montant</th>
                <th className="px-5 py-3">Statut</th>
              </tr>
            </thead>
            <tbody>
              {tx.length === 0 ? (
                <tr><td colSpan="4" className="px-5 py-8 text-center text-sm text-muted-foreground">Aucune transaction pour le moment.</td></tr>
              ) : tx.map(t => (
                <tr key={t.session_id} className="border-b border-border last:border-0 hover:bg-accent/30 transition-colors">
                  <td className="px-5 py-3 font-mono text-xs truncate max-w-[160px]">{t.session_id}</td>
                  <td className="px-5 py-3 capitalize">{t.plan_id}</td>
                  <td className="px-5 py-3 tabular-nums">{t.amount} {t.currency?.toUpperCase()}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                      t.payment_status === "paid" ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"
                    }`}>{t.payment_status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-3 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className={`text-right ${mono ? "font-mono text-[11px] break-all" : "font-medium"}`}>{value}</span>
    </div>
  );
}
function Yes({ ok, warn, label }) {
  const cls = ok ? "text-emerald-600" : warn ? "text-amber-600" : "text-foreground";
  return <span className={`inline-flex items-center gap-1 font-bold ${cls}`}><CheckCircle2 className="h-3 w-3" />{label || "Oui"}</span>;
}
function No({ ok, err, label }) {
  const cls = ok ? "text-emerald-600" : err ? "text-rose-600" : "text-muted-foreground";
  return <span className={`inline-flex items-center gap-1 font-bold ${cls}`}><AlertTriangle className="h-3 w-3" />{label || "Non"}</span>;
}
